// ========================= SEC Tiebreaker Module =========================

const EPS = 1e-9;
const nearlyEqual = (a, b) => a === b || Math.abs(a - b) < EPS;

export const resolveSECties = (tiedTeams, confTeams, games, options = {}) => {
    if (!Array.isArray(tiedTeams) || tiedTeams.length === 0) return [];
    return resolveGroup(tiedTeams, confTeams, games, options);
};

export const resolveSECTie = (tiedTeams, confTeams, games, options = {}) => {
    if (!Array.isArray(tiedTeams) || tiedTeams.length === 0) return [];
    return resolveGroup(tiedTeams, confTeams, games, options);
};

const resolveGroup = (group, confTeams, games, options = {}) => {
    if (group.length === 0) return [];
    if (group.length === 1) return group;
    if (group.length === 2) {
        return resolveTwoTeamTie(group[0], group[1], confTeams, games, options);
    }
    return resolveMultiTeamTie(group, confTeams, games, options);
};

const isCountable = g => !g.exempt && g.conferenceGame;

const mapTeamGames = (team, games) =>
    games
        .filter(g => isCountable(g) && (g.homeTeam === team || g.awayTeam === team))
        .map(g => ({
            opponent: g.homeTeam === team ? g.awayTeam : g.homeTeam,
            won: g.winner === team
        }));

const getConfPct = c => {
    if (typeof c.conferenceWins === 'number' && typeof c.conferenceGames === 'number' && c.conferenceGames > 0) {
        return c.conferenceWins / c.conferenceGames;
    }
    if (typeof c.conferenceWins === 'number') return c.conferenceWins / 8;
    return 0;
};

const splitOnScores = (remaining, scoreOf, confTeams, games, options) => {
    const scores = remaining.map(t => scoreOf(t));
    const maxScore = Math.max(...scores);
    const topGroup = remaining.filter((t, i) => nearlyEqual(scores[i], maxScore));
    const restGroup = remaining.filter((t, i) => !nearlyEqual(scores[i], maxScore));

    if (topGroup.length === 0 || topGroup.length === remaining.length) return null;

    const topOrdered = resolveGroup(topGroup, confTeams, games, options);
    const restOrdered = resolveGroup(restGroup, confTeams, games, options);
    return [...topOrdered, ...restOrdered];
};

// ---------------------------------------------------------------- Multi-team
const resolveMultiTeamTie = (tiedTeams, confTeams, games, options = {}) => {
    const { rng = Math.random } = options;
    let remaining = [...tiedTeams];

    // Step a: Head-to-head winning percentage among tied teams
    const h2hMap = remaining.map(t => {
        const headGames = games.filter(
            g =>
                isCountable(g) &&
                (g.homeTeam === t.team || g.awayTeam === t.team) &&
                remaining.some(r => r.team !== t.team && (g.homeTeam === r.team || g.awayTeam === r.team))
        );
        const wins = headGames.filter(g => g.winner === t.team).length;
        const total = headGames.length;
        const pct = total > 0 ? wins / total : null;
        return { teamObj: t, pct, wins, total };
    });

    const allPlayedEachOther = h2hMap.every(m => m.pct !== null);

    if (allPlayedEachOther) {
        const split = splitOnScores(remaining, t => h2hMap.find(m => m.teamObj.team === t.team).pct, confTeams, games, options);
        if (split) return split;
    } else {
        // If a team defeated every other tied team IT PLAYED (and played at least one), advance it
        const sweptPlayed = remaining.filter(t => {
            const played = remaining.filter(o => o.team !== t.team &&
                games.some(g => isCountable(g) && ((g.homeTeam === t.team && g.awayTeam === o.team) || (g.awayTeam === t.team && g.homeTeam === o.team)))
            );
            if (played.length === 0) return false;
            return played.every(o =>
                games.some(g => isCountable(g) && ((g.homeTeam === t.team && g.awayTeam === o.team) || (g.awayTeam === t.team && g.homeTeam === o.team)) && g.winner === t.team)
            );
        });

        if (sweptPlayed.length === 1) {
            const top = sweptPlayed[0];
            const rest = remaining.filter(r => r.team !== top.team);
            return [...resolveGroup([top], confTeams, games, options), ...resolveGroup(rest, confTeams, games, options)];
        }
    }

    // Step b: Winning percentage vs. all common conference opponents
    const splitB = splitOnScores(remaining, t => winPctVsCommonOpponentsMulti(t.team, remaining, games), confTeams, games, options);
    if (splitB) return splitB;

    // Step c: Winning percentage vs. next-highest placed common opponent (sequential)
    const opponentsByTeam = remaining.map(t => ({
        team: t.team,
        opponents: mapTeamGames(t.team, games).map(g => g.opponent)
    }));
    const commonOpps = opponentsByTeam
        .map(t => t.opponents)
        .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0]?.opponents || []);

    if (commonOpps.length > 0) {
        const sortedCommonOpps = confTeams
            .filter(c => commonOpps.includes(c.team))
            .sort((a, b) => getConfPct(b) - getConfPct(a));

        for (const opp of sortedCommonOpps) {
            const scoreOf = t => {
                const gVsOpp = mapTeamGames(t.team, games).filter(g => g.opponent === opp.team);
                if (!gVsOpp.length) return 0;
                return gVsOpp.filter(g => g.won).length / gVsOpp.length;
            };
            const splitC = splitOnScores(remaining, scoreOf, confTeams, games, options);
            if (splitC) return splitC;
        }
    }

    // Step d: Combined conference winning percentage of all conference opponents
    const splitD = splitOnScores(remaining, t => combinedOppWinPct(t.team, games, confTeams), confTeams, games, options);
    if (splitD) return splitD;

    // Step e: Random draw
    return [...remaining].sort((a, b) => rng() - rng());
};

// ---------------------------------------------------------------- Two-team
const resolveTwoTeamTie = (A, B, confTeams, games, options = {}) => {
    const { rng = Math.random } = options;

    // 1. Head-to-head
    const matches = games.filter(g => isCountable(g) && ((g.homeTeam === A.team && g.awayTeam === B.team) || (g.homeTeam === B.team && g.awayTeam === A.team)));
    if (matches.length > 0) {
        const aWins = matches.filter(m => m.winner === A.team).length;
        const bWins = matches.filter(m => m.winner === B.team).length;
        if (aWins > bWins) return [A, B];
        if (bWins > aWins) return [B, A];
    }

    // 2. Win % vs all common conference opponents
    const co = twoTeamCommonOppPct(A.team, B.team, games);
    if (co !== null) return co > 0 ? [A, B] : [B, A];

    // 3. Win % vs next-highest placed common opponent in standings (sequential)
    const Amap = mapTeamGames(A.team, games);
    const Bmap = mapTeamGames(B.team, games);
    const Aopps = new Set(Amap.map(g => g.opponent));
    const Bopps = new Set(Bmap.map(g => g.opponent));
    const common = [...Aopps].filter(o => Bopps.has(o));

    if (common.length > 0) {
        const sortedCommon = confTeams
            .filter(c => common.includes(c.team))
            .sort((a, b) => getConfPct(b) - getConfPct(a));

        for (const opp of sortedCommon) {
            const aVs = Amap.filter(g => g.opponent === opp.team);
            const bVs = Bmap.filter(g => g.opponent === opp.team);
            const aPct = aVs.length ? aVs.filter(g => g.won).length / aVs.length : 0;
            const bPct = bVs.length ? bVs.filter(g => g.won).length / bVs.length : 0;
            if (!nearlyEqual(aPct, bPct)) {
                return aPct > bPct ? [A, B] : [B, A];
            }
        }
    }

    // 4. Combined conf win % of conference opponents
    const aCombined = combinedOppWinPct(A.team, games, confTeams);
    const bCombined = combinedOppWinPct(B.team, games, confTeams);
    if (!nearlyEqual(aCombined, bCombined)) return aCombined > bCombined ? [A, B] : [B, A];

    // 5. Random draw
    return rng() < 0.5 ? [A, B] : [B, A];
};

const twoTeamCommonOppPct = (Ateam, Bteam, games) => {
    const Amap = mapTeamGames(Ateam, games);
    const Bmap = mapTeamGames(Bteam, games);
    const Aopps = new Set(Amap.map(g => g.opponent));
    const Bopps = new Set(Bmap.map(g => g.opponent));
    const common = [...Aopps].filter(o => Bopps.has(o));
    if (!common.length) return null;

    const Agames = Amap.filter(g => common.includes(g.opponent));
    const Bgames = Bmap.filter(g => common.includes(g.opponent));
    const Apct = Agames.length ? Agames.filter(g => g.won).length / Agames.length : 0;
    const Bpct = Bgames.length ? Bgames.filter(g => g.won).length / Bgames.length : 0;
    if (nearlyEqual(Apct, Bpct)) return null;
    return Apct > Bpct ? 1 : -1;
};

const winPctVsCommonOpponentsMulti = (team, tiedTeams, games) => {
    const opponentsByTeam = tiedTeams.map(t => ({
        team: t.team,
        opponents: mapTeamGames(t.team, games).map(g => g.opponent)
    }));
    const commonOpps = opponentsByTeam
        .map(t => t.opponents)
        .reduce((acc, opps) => acc.filter(o => opps.includes(o)), opponentsByTeam[0]?.opponents || []);
    if (!commonOpps.length) return 0;
    const teamGames = mapTeamGames(team, games).filter(g => commonOpps.includes(g.opponent));
    if (!teamGames.length) return 0;
    return teamGames.filter(g => g.won).length / teamGames.length;
};

const combinedOppWinPct = (team, games, confTeams) => {
    const opponents = mapTeamGames(team, games).map(g => g.opponent);
    if (!opponents.length) return 0;
    const oppPcts = opponents.map(op => {
        const rec = confTeams.find(c => c.team === op);
        return rec ? getConfPct(rec) : 0;
    });
    return oppPcts.reduce((sum, p) => sum + p, 0) / opponents.length;
};